import type {
  DesignBody,
  FaceKey,
  QuoteDesignViewFont,
  QuoteDesignViewInk,
  ResolvedEditAreaDefinition,
} from '@repo/api-contracts';
import {
  type CreateContext,
  type FacePreviewInput,
  type FacePreviewResult,
  fontString,
  type MeasureText,
  renderFacePreview,
} from '@repo/design-renderer';
import type { Raster } from '@repo/warp-engine';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createCanvasContext, createCanvasMeasure } from '../canvas-raster';
import { loadImageRaster } from '../load-image-raster';
import { createRasterCache } from '../materials-cache';
import { registerFontFace } from '../register-font-face';
import {
  createFontRegistry,
  type FaceRenderNotice,
  type RegisterFont,
  renderFace,
  VIEWER_FALLBACK_FONT_FAMILY,
} from './render-face';

/** 뷰어 렌더 상한(V8) — 사진·마스크·음영·고객 이미지를 이 픽셀 수 안으로 줄여 받고 그린다. */
export const VIEWER_MAX_PIXELS = 2_000_000;

/**
 * 페이지에 하나뿐인 폰트 등록 — 창을 닫았다 다시 열어도 같은 q-{fontId} FontFace 를 document.fonts 에 다시 넣지 않는다.
 * 실패한 폰트는 기억하지 않아 다음 그리기에서 다시 받는다.
 */
const sharedFontRegistry = createFontRegistry(registerFontFace);

/**
 * 앱 기본 글꼴(Pretendard)을 그릴 글자의 유니코드 범위 조각까지 받아 둔다(V9). 두 앱은 @repo/ui 테마로 이 웹 글꼴을 싣는다.
 * document.fonts 가 없거나 글꼴을 못 받으면 던지고, renderFace 가 무시해 브라우저 기본 글꼴로 그린다.
 */
const loadAppFallbackFont = async (text: string): Promise<void> => {
  await document.fonts.load(fontString(16, VIEWER_FALLBACK_FONT_FAMILY), text);
};

/** 테스트 대역 — 비운 것은 브라우저 구현을 쓴다. */
export interface FaceRenderDeps {
  readonly loadRaster?: (url: string) => Promise<Raster>;
  /** 주면 이 마운트만의 폰트 등록을 쓴다(테스트). 비우면 페이지에 하나뿐인 등록을 쓴다. */
  readonly registerFont?: RegisterFont;
  readonly loadFallbackFont?: (text: string) => Promise<void>;
  readonly render?: (input: FacePreviewInput) => FacePreviewResult;
  readonly createContext?: CreateContext;
  readonly measure?: MeasureText;
}

export interface UseFaceRenderInput {
  readonly enabled: boolean;
  readonly definition: ResolvedEditAreaDefinition;
  readonly colorOptionId: string;
  readonly faceKey: FaceKey;
  readonly body: DesignBody;
  readonly assetUrls: ReadonlyMap<string, string>;
  readonly fonts: readonly QuoteDesignViewFont[];
  readonly inks: readonly QuoteDesignViewInk[];
  readonly refreshUrls: () => Promise<void>;
  readonly maxPixels?: number;
  readonly deps?: FaceRenderDeps;
}

export interface FaceRenderState {
  readonly status: 'idle' | 'rendering' | 'ready' | 'failed';
  readonly raster: Raster | null;
  readonly notices: readonly FaceRenderNotice[];
  readonly retry: () => void;
}

type FaceEntry = Omit<FaceRenderState, 'retry'>;

const IDLE: FaceEntry = { status: 'idle', raster: null, notices: [] };
const RENDERING: FaceEntry = { status: 'rendering', raster: null, notices: [] };
const FAILED: FaceEntry = { status: 'failed', raster: null, notices: [] };

const cacheKeyOf = (input: { colorOptionId: string; faceKey: FaceKey }): string =>
  `${input.colorOptionId}:${input.faceKey}`;

/**
 * 고른 면 하나를 그리고 색상·면별로 캐시한다(V8) — 고정 사본은 바뀌지 않으므로 같은 면은 다시 그리지 않는다.
 * 주소 갱신(V13)으로 정의·주소 객체가 바뀌어도 다시 그리지 않는다 — 나머지 입력은 latest ref 로 읽는다.
 * 실패한 면만 failed 로 남고 retry 가 그 면을 다시 그린다. 다른 면의 결과는 그대로다.
 */
export const useFaceRender = (input: UseFaceRenderInput): FaceRenderState => {
  const maxPixels = input.maxPixels ?? VIEWER_MAX_PIXELS;
  // biome-ignore lint/correctness/useExhaustiveDependencies: 대역·재료 캐시는 마운트 때 한 번만 만든다.
  const tools = useMemo(() => {
    const deps = input.deps ?? {};
    const loadRaster = deps.loadRaster ?? ((url: string) => loadImageRaster(url, { maxPixels }));
    return {
      rasterCache: createRasterCache(loadRaster),
      fontRegistry:
        deps.registerFont === undefined
          ? sharedFontRegistry
          : createFontRegistry(deps.registerFont),
      loadFallbackFont: deps.loadFallbackFont ?? loadAppFallbackFont,
      render: deps.render ?? renderFacePreview,
      createContext: deps.createContext ?? createCanvasContext,
      measure: deps.measure ?? createCanvasMeasure(),
    };
  }, []);
  const latest = useRef(input);
  latest.current = input;
  const [entries, setEntries] = useState<ReadonlyMap<string, FaceEntry>>(() => new Map());
  // 시작한 면(진행 중·끝남) — 효과가 다시 돌아도(StrictMode 포함) 같은 면을 겹쳐 그리지 않는다.
  const started = useRef(new Set<string>());
  const [attempt, setAttempt] = useState(0);
  const key = cacheKeyOf(input);

  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt 는 다시 시도 신호다. 나머지 입력은 latest ref 로 읽는다.
  useEffect(() => {
    if (!input.enabled || started.current.has(key)) return;
    started.current.add(key);
    const settle = (entry: FaceEntry): void =>
      setEntries((previous) => new Map(previous).set(key, entry));
    const { faceKey, colorOptionId } = latest.current;
    renderFace({
      faceKey,
      colorOptionId,
      maxPixels,
      sources: () => latest.current,
      refreshUrls: () => latest.current.refreshUrls(),
      ...tools,
    }).then(
      (face) => settle({ status: 'ready', raster: face.raster, notices: face.notices }),
      () => settle(FAILED),
    );
  }, [input.enabled, key, attempt, tools, maxPixels]);

  const failed = entries.get(key)?.status === 'failed';
  const retry = useCallback(() => {
    if (!failed) return;
    started.current.delete(key);
    setEntries((previous) => {
      const next = new Map(previous);
      next.delete(key);
      return next;
    });
    setAttempt((count) => count + 1);
  }, [failed, key]);

  if (!input.enabled) return { ...IDLE, retry };
  return { ...(entries.get(key) ?? RENDERING), retry };
};
